import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  useAvailableConfigFiles,
  useConfigFile,
  useSaveConfigFile,
} from './configuration.api';
import {
  CONFIG_FILES,
  inferKind,
  rowError,
  toFileText,
  toRows,
  type ConfigFileKey,
  type SettingRow,
} from './types';

/**
 * The blob-storage configuration editors, as one page.
 *
 * There were three routes — `/configuration/server`, `/app`, `/manager` —
 * behind three sidebar entries, all three rendering the same 445-line component
 * with a different container and filename. The four-line wrappers around it
 * differed only in which string they passed.
 *
 * The file is a picker here, and the sidebar no longer probes blob storage on
 * every render to decide which of the three entries to draw.
 */
export function ConfigurationPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const requested = params.get('file');
  const [file, setFile] = useState<ConfigFileKey>(
    CONFIG_FILES.some((entry) => entry.key === requested)
      ? (requested as ConfigFileKey)
      : 'server'
  );

  const [rows, setRows] = useState<SettingRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [term, setTerm] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [adding, setAdding] = useState(false);

  const { available, isPending: probing } = useAvailableConfigFiles();
  const content = useConfigFile(file);
  const save = useSaveConfigFile(file);

  /** Re-seeded whenever the file changes or the server hands back new text. */
  useEffect(() => {
    if (content.data === undefined) return;
    try {
      const parsed = content.data ? JSON.parse(content.data) : {};
      setRows(toRows(parsed as Record<string, unknown>));
    } catch {
      setRows([]);
    }
    setDirty(false);
  }, [content.data, file]);

  const selectFile = (next: ConfigFileKey) => {
    setFile(next);
    setTerm('');
    setParams((current) => {
      const updated = new URLSearchParams(current);
      updated.set('file', next);
      return updated;
    }, { replace: true });
  };

  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(
      (row) =>
        row.key.toLowerCase().includes(needle) ||
        row.text.toLowerCase().includes(needle)
    );
  }, [rows, term]);

  const problems = rows.filter((row) => rowError(row) !== null);
  const canSave = dirty && problems.length === 0 && !save.isPending;

  const update = (key: string, text: string) => {
    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, text } : row))
    );
    setDirty(true);
  };

  const remove = (key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
    setDirty(true);
  };

  function add() {
    if (rows.some((row) => row.key === newKey.trim())) {
      toast.error(t('configuration.keyExists'));
      return;
    }
    setRows((current) => [
      ...current,
      { key: newKey.trim(), text: newValue, kind: inferKind(newValue) },
    ]);
    setDirty(true);
    setAdding(false);
    setNewKey('');
    setNewValue('');
  }

  function submit() {
    save.mutate(toFileText(rows), {
      onSuccess: () => toast.success(t('configuration.saved')),
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : t('configuration.saveFailed')
        ),
    });
  }

  function download() {
    const blob = new Blob([toFileText(rows)], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = CONFIG_FILES.find((entry) => entry.key === file)!.file;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  const missing = !probing && !available.includes(file);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('configuration.title')}
        description={t('configuration.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={content.isFetching}
              onClick={() => void content.refetch()}
            >
              <RefreshCw className={content.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={rows.length === 0}
              onClick={download}
            >
              <Download className="size-3.5" />
              <span className="hidden sm:inline">
                {t('configuration.download')}
              </span>
            </Button>
            <Button size="sm" disabled={!canSave} onClick={submit}>
              {save.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              {t('common.save')}
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="sm:w-56">
          <Field label={t('configuration.file')}>
            <Select
              value={file}
              disabled={content.isFetching}
              onValueChange={(value) => selectFile(value as ConfigFileKey)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONFIG_FILES.map((entry) => (
                  <SelectItem key={entry.key} value={entry.key}>
                    {t(`configuration.file_${entry.key}`)}
                    {!probing && !available.includes(entry.key) && (
                      <span className="text-muted-foreground">
                        {' '}
                        — {t('configuration.missingShort')}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="flex-1">
          <Field label={t('configuration.search')}>
            <div className="relative">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder={t('configuration.searchPlaceholder')}
                aria-label={t('configuration.searchPlaceholder')}
                className="pl-9"
              />
            </div>
          </Field>
        </div>
      </div>

      {dirty && (
        <p className="border-warning/40 bg-warning/10 text-warning rounded-xl border px-4 py-2.5 text-sm">
          {t('configuration.unsaved')}
        </p>
      )}

      {problems.length > 0 && (
        <p className="border-destructive/30 bg-destructive/8 text-destructive rounded-xl border px-4 py-2.5 text-sm">
          {t('configuration.invalid', { count: problems.length })}
        </p>
      )}

      {content.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('configuration.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void content.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {content.isPending && <Skeleton className="h-64 w-full rounded-xl" />}

      {missing && content.isSuccess && (
        <div className="relative rounded-xl">
          <BusyOverlay show={content.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('configuration.missing', {
              file: CONFIG_FILES.find((entry) => entry.key === file)!.file,
            })}
          </p>
        </div>
      )}

      {content.isSuccess && !missing && (
        <>
          <div className="flex items-center justify-between gap-3">
            <p className="text-muted-foreground text-sm">
              {t('configuration.settingCount', {
                shown: visible.length,
                total: rows.length,
              })}
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setAdding(true)}>
                <Plus className="size-3.5" />
                {t('configuration.addSetting')}
              </Button>
              {rows.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                      <span className="hidden sm:inline">
                        {t('configuration.removeAll')}
                      </span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('configuration.removeAll')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('configuration.removeAllConfirm', {
                          count: rows.length,
                        })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t('common.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => {
                          setRows([]);
                          setDirty(true);
                        }}
                      >
                        {t('configuration.removeAll')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>

          {/* A reload re-seeds these rows from the server, so they sit under
              the spinner rather than staying editable while it runs. */}
          <div className="relative rounded-xl">
            <BusyOverlay show={content.isFetching} />
            <div
              className={cn(
                content.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.length === 0 ? (
                <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
                  {t('configuration.empty')}
                </p>
              ) : (
                <div className="divide-y rounded-xl border">
                  {visible.map((row) => {
                    const problem = rowError(row);
                    const multiline = row.kind === 'json';
                    return (
                      <div key={row.key} className="space-y-1.5 p-3">
                        <div className="flex items-center gap-2">
                          <code className="min-w-0 flex-1 truncate text-xs font-medium">
                            {row.key}
                          </code>
                          <Badge variant="secondary">
                            {t(`configuration.kind_${row.kind}`)}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('configuration.removeSetting')}
                            onClick={() => remove(row.key)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                        {multiline ? (
                          <Textarea
                            rows={4}
                            value={row.text}
                            aria-label={row.key}
                            className={cn(
                              'font-mono text-xs',
                              problem === 'value' && 'border-destructive'
                            )}
                            onChange={(event) => update(row.key, event.target.value)}
                          />
                        ) : (
                          <Input
                            value={row.text}
                            aria-label={row.key}
                            className={cn(
                              'font-mono text-xs',
                              problem === 'value' && 'border-destructive'
                            )}
                            onChange={(event) => update(row.key, event.target.value)}
                          />
                        )}
                        {problem === 'value' && (
                          <p className="text-destructive text-xs">
                            {t(`configuration.invalid_${row.kind}`)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <AlertDialog open={adding} onOpenChange={setAdding}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('configuration.addSetting')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('configuration.addBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <Field label={t('configuration.key')} htmlFor="newKey">
              <Input
                id="newKey"
                value={newKey}
                className="font-mono text-xs"
                onChange={(event) => setNewKey(event.target.value)}
              />
            </Field>
            <Field
              label={t('configuration.value')}
              htmlFor="newValue"
              hint={t('configuration.willBeStoredAs', {
                kind: t(`configuration.kind_${inferKind(newValue)}`),
              })}
            >
              <Input
                id="newValue"
                value={newValue}
                className="font-mono text-xs"
                onChange={(event) => setNewValue(event.target.value)}
              />
            </Field>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={!newKey.trim()}
              onClick={(event) => {
                event.preventDefault();
                add();
              }}
            >
              {t('configuration.addSetting')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
